import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import styled, {useTheme} from 'styled-components';
import {Icon, Loader} from 'shared/helpers';
import {Formik} from 'formik';
import {useProductDimension} from 'components/customer/JobDashboard/helpers/useProductDimension';
import {DimensionInput} from 'components/customer/JobDashboard/columns/DimensionInput';
import {Product} from 'components/customer/Product/entity/Product';
import {DimensionUpdateHandler} from 'components/customer/JobDashboard/columns/DimensionUpdateHandler';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';

interface DimensionsProps {
    productDetails: Product;
    hideDimensions?: boolean;
    setShowForm?: Dispatch<SetStateAction<boolean>>;
}

export const Dimensions = ({
    productDetails,
    hideDimensions = false,
    setShowForm,
}: DimensionsProps) => {
    const {job} = useJobContext() as PartialJob;
    const theme = useTheme();
    const {
        dimensions,
        fields,
        showForm,
        validator,
        savingProduct,
        formHandler,
        toggleForm,
    } = useProductDimension(productDetails);

    const form = useRef<HTMLFormElement>();
    const submitForm = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        form.current.requestSubmit();
    }, []);

    useEffect(() => {
        if (typeof setShowForm === 'function') {
            setShowForm(showForm);
        }
    }, [showForm]);

    if (showForm) {
        return (
            <FormContainer
                $showForm={showForm}
                $hideDimensions={hideDimensions}>
                <Formik
                    initialValues={dimensions}
                    enableReinitialize={true}
                    validationSchema={validator}
                    validateOnChange={false}
                    onSubmit={formHandler}>
                    {({handleSubmit}) => (
                        <form onSubmit={handleSubmit} noValidate ref={form}>
                            <Container $form={false}>
                                <DimensionUpdateHandler
                                    productId={Number(
                                        productDetails.job_cabinet_id
                                    )}
                                />
                                {fields && fields.length > 0
                                    ? fields.map((field) => {
                                          return (
                                              <DimensionInput
                                                  style={{
                                                      display: field.hidden
                                                          ? 'none'
                                                          : 'flex',
                                                  }}
                                                  key={field.name}
                                                  id={Number(productDetails.id)}
                                                  fieldData={field}
                                              />
                                          );
                                      })
                                    : null}

                                <ButtonContainer>
                                    <Icon
                                        onClick={submitForm}
                                        iconName="Button-Tick-Secondary.svg"
                                        color="primary"
                                        title="Save changes"
                                        style={{cursor: 'pointer'}}
                                    />
                                    <Icon
                                        onClick={toggleForm}
                                        iconName="Button-Error-Primary.svg"
                                        color={theme.colors.errorLabel.main}
                                        title="Cancel"
                                        style={{cursor: 'pointer'}}
                                    />
                                </ButtonContainer>
                            </Container>
                        </form>
                    )}
                </Formik>
            </FormContainer>
        );
    }

    return (
        <Container $hideDimensions={hideDimensions}>
            {savingProduct ? <Loader loader={savingProduct} /> : null}
            {hideDimensions ? null : <div>{productDetails.dimensions}</div>}
            {job?.status == JOB_STATUS.PROCESSING ? (
                <EditButton onClick={toggleForm} title="Quick edit dimensions">
                    Edit Sizes
                </EditButton>
            ) : null}
        </Container>
    );
};

const FormContainer = styled.div<{
    $showForm: boolean;
    $hideDimensions?: boolean;
}>`
    ${({$showForm, $hideDimensions = false}) => {
        if ($showForm && $hideDimensions) {
            return `
                width: 60%;
            `;
        }
    }}
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    justify-content: center;
    gap: 10px;

    button {
        width: fit-content;
        height: fit-content;
        border: 0;
        background: transparent;
        padding: 0;
        margin: 0;
    }

    button > img,
    > img {
        width: 22px !important;
    }
`;

const Container = styled.div<{$form?: boolean; $hideDimensions?: boolean}>`
    display: flex;
    min-width: ${({$hideDimensions = false}) =>
        $hideDimensions ? 'fit-content' : '160px'};
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding-top: ${({$form = true, $hideDimensions = false}) =>
        $form && !$hideDimensions ? '39px' : 0};

    > img {
        width: 18px;
    }
`;

const EditButton = styled.button`
    border: none;
    background: ${({theme}) => theme.colors.primary.main};
    color: white;
    border-radius: 14px;
    padding: 5px 10px;
    font-weight: 500;
    box-shadow: 3px 3px 5px 0px #9f9f9f;
    transition: 200ms background;

    &:hover {
        background: ${({theme}) => theme.colors.secondary.main};
    }

    &:active {
        box-shadow: 3px 1px 5px 0px #9f9f9f;
        transform: translateY(2px);
    }
`;
